import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

import { SessionExpiredDialog } from '@/components/organisms/SessionExpiredDialog'

import { redirectToPiloteSignIn } from '../redirectToPiloteSignIn.ts'

import { dismissSessionDialog, isEmbeddedSession, useSessionPhase } from './sessionStore.ts'
import { useSessionActivity } from './useSessionActivity.ts'

const SETTLE_TIMEOUT_MS = 5000
const SETTLE_POLL_MS = 100

// An action refused at the moment the session lapsed may still be undoing what it wrote, such as
// the "in progress" mark on a transport request; leaving the page now would strand that mark.
const waitForMutationsToSettle = (queryClient: QueryClient) =>
  new Promise<void>((resolve) => {
    const deadline = Date.now() + SETTLE_TIMEOUT_MS
    const check = () => {
      if (queryClient.isMutating() === 0 || Date.now() >= deadline) {
        resolve()
        return
      }
      setTimeout(check, SETTLE_POLL_MS)
    }
    check()
  })

export const SessionGuard = () => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const phase = useSessionPhase()
  useSessionActivity()
  const labels = useMemo(
    () => ({
      title: intl.formatMessage({ id: 'session.expired.title' }),
      body: intl.formatMessage({ id: 'session.expired.body' }),
      signIn: intl.formatMessage({ id: 'session.expired.signIn' }),
      close: intl.formatMessage({ id: 'session.expired.close' }),
    }),
    [intl]
  )

  if (isEmbeddedSession()) {
    return null
  }

  return (
    <SessionExpiredDialog
      open={phase === 'dialog-open'}
      labels={labels}
      onSignIn={() => {
        handleNotAwaitedPromise(waitForMutationsToSettle(queryClient).then(redirectToPiloteSignIn))
      }}
      onClose={dismissSessionDialog}
    />
  )
}
